import { countryCode, currencyCode } from 'Helpers/Geo/Country';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';

/**
 * The cart is priced in the country and currency of the IP that loaded the
 * page: `window.countryData` is read once, at load, and never refreshed.
 * Amen prices the order in the country and currency of the IP that sends
 * create-order. If the IP changes in between (a VPN hop, a network change),
 * the two differ, and amen rejects the order with LOCATION_CHANGED.
 *
 * This module sends the location with create-order, and reloads the page once
 * when amen rejects the order, so that the page takes the new location.
 */

const STORAGE_KEY = 'fan-location-reload';

// One reload for each window. A second LOCATION_CHANGED inside the window
// shows the message and does not reload, so the page cannot reload in a loop.
const RELOAD_WINDOW_MS = 30 * 60 * 1000;

// Is the page open with a `?cc=` country override? The override sets
// `window.countryData`, but create-order does not send `cc`, so amen would
// always see a different location. Read at load: in-app navigation drops the
// query string but `window.countryData` keeps the override.
const hasCountryOverride = !!getUrlParameter('cc');

let reloadStarted = false;

/**
 * The location fields for the create-order request.
 * Not sent with a `?cc=` override: amen does not check the request then.
 * @returns {Object} { countryCode, currencyCode } or {}
 */
export const locationOrderFields = () => {
  if (hasCountryOverride || getUrlParameter('cc')) {
    return {};
  }

  return { countryCode, currencyCode };
};

/**
 * Take the reload for this window, if there is one to take.
 * Fails closed: when the storage is not usable, the guard cannot remember
 * the reload, so this gives false and the page does not reload.
 * @returns {boolean} true when the caller must reload now
 */
const takeReload = () => {
  try {
    const now = Date.now();
    const last = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');

    // A time that is in the future (the clock moved back) also blocks the reload.
    if (last && Number.isFinite(last.at) && now - last.at < RELOAD_WINDOW_MS) {
      return false;
    }

    const record = JSON.stringify({ at: now, countryCode, currencyCode });
    window.sessionStorage.setItem(STORAGE_KEY, record);

    // Read it back: a storage that accepts writes and forgets them would loop.
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)).at === now;
  } catch (e) {
    return false;
  }
};

/**
 * Amen rejected create-order with LOCATION_CHANGED. Reload the page, once,
 * to take the new `window.countryData`.
 * @returns {boolean} true when the page is reloading. false when the reload
 *                    was refused (one already happened in the window): the
 *                    caller shows the message.
 */
export const reloadForLocationChange = () => {
  if (reloadStarted) {
    return true;
  }

  if (!takeReload()) {
    return false;
  }

  reloadStarted = true;
  window.location.reload();

  return true;
};

/**
 * An order was created, so the location is in sync: allow a reload for a
 * later change.
 */
export const clearLocationReload = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};
